<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <component :is="cardType" v-bind="{ ...propTypes, ...tileClasses }">
      <ImageCard :isLarge="isLargeImage">
        <template #image="{ widthView, aspectRatio }">
          <media-display
            :aspect-ratio="aspectRatio"
            :fraction="widthView"
            :sc-image-field="imageField"
            :image-desktop-source="setImageSrc"
            :video-source="videoUrl"
            :video-loop="loop"
            :video-hidden-text="hiddenVideoTitle"
            @media-error="isVideoFailed = true"
          />
        </template>
        <template #title>
          <heading v-if="hasTitle"
            class="title"
            desktop-size="size-5"
            mobile-size="size-5"
            :textField="fields.title"
            :tagField="headingSize"
            default-tag="h3"
          />
        </template>
        <template #description>
          <sc-text v-if="hasDescription" class="description" :field="fields.description" />
        </template>
        <template #tag>
          <sc-text v-if="hasTag" class="tag" :field="fields.tag" />
        </template>
        <template #date>
          <sc-text v-if="hasDate" class="data" :field="fields.date" />
        </template>
        <template #location>
          <sc-text v-if="hasLocation" class="location" :field="fields.location" />
        </template>
        <template #default>
          <sc-link v-if="isEditMode" class="sc-link" :field="fields.cta" />
          <sh-button
            v-if="tileLink && ctaAppearance && !isEditMode"
            class="link"
            :role="!isVideo && 'button'"
            v-bind="hasCtaAttrs"
            :color="buttonColor"
            :[setStyle]="true"
          >
            {{ fields.cta.value.text }}
          </sh-button>
        </template>
      </ImageCard>
    </component>
  </sc-render>
</template>

<script>
import { Image, Text, Link } from '@sitecore-jss/sitecore-jss-vue'
import { ImageCard } from '@sohohouse/design-system'
import { createNamespacedHelpers } from 'vuex'

import { pathToRegexp, compile } from 'path-to-regexp'
import mapFields from '~/helpers/map-fields'
import { getButtonType } from '~/helpers/get-button-type'
import getButtonStyle from '~/helpers/get-button-style'
import CTA from '~/constants/appearance'
import ShLink from '~/components/sh-link'
import get from '~/helpers/get'
import { decreaseTagValue } from '~/helpers/decrease-tag-value'
import matchRatio from '~/helpers/match-ratio'
import ThemeMixin from '~/mixins/theme'

import MediaDisplay from '~/components/sitecore/media-display'
import ScRender from '../sc-render'
import Heading from '../heading'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'CarouselImageTile',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  mixins: [ThemeMixin],

  data () {
    return {
      isVideoFailed: false
    }
  },

  components: {
    ScImage: Image,
    ScLink: Link,
    ScText: Text,
    ShLink,
    ScRender,
    Heading,
    ImageCard,
    MediaDisplay
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      title: 'title',
      description: 'description',
      tag: 'tag',
      date: 'date',
      location: 'location',
      imageField: 'image',
      setImageSrc: 'image.value.src',
      imageHeight: 'image.value.height',
      imageWidth: 'image.value.width',
      ctaHref: 'cta.value.href',
      ctaType: 'cta.value.linktype',
      videoUrl: 'mainVideo.value',
      hiddenVideoTitle: 'hiddenVideoTitle.value',
      ctaAppearance: 'ctaAppearance.value'
    }),
    // replacing on BE side impossible until total migration from perks to member-benefits
    // todo: remove this after perks will be totally migrated
    tileLink () {
      const reg = pathToRegexp('/perks/:benefit')
      const params = reg.exec(this.ctaHref)

      if (!params || !params[1]) return this.ctaHref

      return compile('/member-benefits/:benefit')({ benefit: params[1] })
    },

    loop () {
      return get(this.fields, 'loop.value', false)
    },

    headingSize () {
      if (this.isEditMode) {
        return decreaseTagValue(get(this.$parent, 'rendering.fields.headingSize'))
      }
      return decreaseTagValue(get(this.fields, 'headingSize'))
    },

    ctaTarget () {
      return get(this.fields, 'cta.value.target', '')
    },

    hasCtaAttrs () {
      const ctaType = getButtonType(this.tileLink)

      return this.isVideo ? { [ctaType]: this.tileLink, target: this.ctaTarget } : {}
    },

    hasDescription () {
      return this.isEditMode || this.description.value
    },

    hasTitle () {
      return this.isEditMode || this.title.value
    },

    hasTag () {
      return this.isEditMode || this.tag.value
    },

    hasDate () {
      return this.isEditMode || this.date.value
    },

    hasLocation () {
      return this.isEditMode || this.location.value
    },

    buttonColor () {
      return this.isDarkTheme ? CTA.SECONDARY : CTA.PRIMARY
    },

    setStyle () {
      return getButtonStyle(this.ctaAppearance)
    },

    isVideo () {
      return this.videoUrl && !this.isVideoFailed
    },

    cardType () {
      if (!this.tileLink || this.isVideo || this.isEditMode) return 'div'

      return 'sh-link'
    },

    propTypes () {
      if (this.cardType === 'div') {
        return { tabindex: '0' }
      }

      return {
        to: this.tileLink,
        target: this.ctaTarget
      }
    },

    tileClasses () {
      return {
        class: [
          'tile',
          {
            'tile-link': this.tileLink && !this.isVideo,
            'tile-editor': this.isEditMode
          }
        ]
      }
    },

    isLargeImage () {
      return matchRatio({
        ratioWidth: 3,
        ratioHeight: 4,
        width: this.imageWidth,
        height: this.imageHeight,
        measurementError: 2
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.tile-editor {
  margin-right: $space-050;
}

a {
  text-decoration: none;
}
</style>
